
import React, { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import AuthForm from '@/components/auth/AuthForm';
import { useAuth } from '@/contexts/AuthContext';
import { getUserCourses } from '@/lib/user-courses';

interface AuthRequiredModalProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const CHECKOUT_STORAGE_KEY = 'app.checkout.courseId';

const AuthRequiredModal = ({ onSuccess, onCancel }: AuthRequiredModalProps) => {
  const [open, setOpen] = React.useState(true);
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Save course ID for the checkout flow
  useEffect(() => {
    if (courseId) {
      localStorage.setItem(CHECKOUT_STORAGE_KEY, courseId);
      console.log("Saved course ID for checkout flow:", courseId);
    }
  }, [courseId]);

  // Check if user is already enrolled after successful authentication
  useEffect(() => {
    const checkEnrollment = async () => {
      if (!user || !courseId) return;

      try {
        const userCourses = await getUserCourses(user.id);
        const isEnrolled = userCourses.some(course => course.course_id === courseId);
        
        if (isEnrolled) {
          console.log("Kasutaja autenditud ja juba registreeritud, suunan õppekeskkonda");
          setOpen(false);
          navigate(`/learner/course/${courseId}`);
        } else {
          // User authenticated but not enrolled, continue with checkout
          onSuccess();
        }
      } catch (error) {
        console.error("Viga registreeringu kontrollimisel pärast autentimist:", error);
        // Still call onSuccess to proceed with checkout
        onSuccess();
      }
    };

    if (user) {
      checkEnrollment();
    }
  }, [user, courseId, navigate, onSuccess]);

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    if (!open) {
      // When modal is closed, navigate to Kursused page instead of course detail
      navigate('/courses');
    }
  };

  const handleAuthSuccess = () => {
    // Don't close the modal or call onSuccess here
    // The useEffect will handle the redirect or checkout continue logic
  };
  
  // Generate redirect URL for the confirmation email
  const currentPath = location.pathname;
  const redirectUrl = `${window.location.origin}/email-confirm-success?redirect_to=${encodeURIComponent(currentPath)}`;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Sisselogimine</DialogTitle>
          <DialogDescription>
            Kursuse ostmiseks on vaja sisse logida või konto luua
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <AuthForm 
            onResetClick={() => {}} 
            onSuccess={handleAuthSuccess}
            redirectUrl={redirectUrl}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthRequiredModal;
